import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Alert,
    Box,
    Card,
    useTheme,
    Grid
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import ReactECharts from 'echarts-for-react';

const BuildingDetailsInsights = ({ id }) => {
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [chartsData, setChartsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        axios
            .get(`/buildings/${id}/renovations-calculations`)
            .then((response) => {
                setData(response.data.overall);
                setChartsData(response.data);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
                setChartsData(null);
            });
    }, [id]);

    const getChartOptions = (type, title) => {
        if (!chartsData || !chartsData.charts || !chartsData.charts[type]) return {};
        const { labels, values } = chartsData.charts[type];
        const filteredData = labels.map((label, idx) => ({ label, value: values[idx] }))
            .filter(item => item.value > 0); // Only show items with values > 0
        return {
            tooltip: {
                trigger: 'item',
                formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            legend: {
                orient: 'vertical',
                left: 'left'
            },
            series: [
                {
                    name: title,
                    type: 'pie',
                    radius: '50%',
                    data: filteredData.map(item => ({ value: item.value, name: item.label }))
                }
            ]
        };
    };

    const renderChart = (type, title, totalKey) => {
        const formatValue = (value) => {
            if (totalKey === 'total_cost') {
                return `€${value?.toLocaleString()}`;
            } else if (totalKey === 'total_emissions') {
                return `${value?.toLocaleString()} kg CO₂`;
            } else {
                return `${value?.toLocaleString()} kWh`;
            }
        };

        return (
            <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                    {title}
                    {/* <br />Total: {formatValue(data?.[totalKey])} */}
                </Typography>
                <ReactECharts option={getChartOptions(type, title)} />
            </Grid>
        );
    };

    return (
        <Card elevation={3} sx={{ mt: 4, borderRadius: 3 }}>
            <Accordion
                expanded={expanded}
                onChange={() => setExpanded(!expanded)}
                sx={{ borderRadius: 3, overflow: 'hidden' }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        px: 3,
                        py: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "#fff" }}>
                        Baseline Assessment
                    </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ backgroundColor: "#f5f9f6", p: 3 }}>
                    {loading ? (
                        <Box sx={{ textAlign: "center", py: 2 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error">Failed to load reduction results.</Alert>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold" }}>Baseline Results</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Energy consumption (kWh)</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>CO₂ emissions (kg CO₂)</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Energy costs (€)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>1</TableCell>
                                        <TableCell>{data.total_kWh}</TableCell>
                                        <TableCell>{data.total_emissions}</TableCell>
                                        <TableCell>{data.total_cost}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            {chartsData && chartsData.charts && (
                                <Grid container spacing={2} mt={3}>
                                    {renderChart('energy', 'Energy Consumption', 'total_kWh')}
                                    {renderChart('emissions', 'Emissions', 'total_emissions')}
                                    {renderChart('costs', 'Energy Costs', 'total_cost')}
                                </Grid>
                            )}
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
        </Card>
    );
};

export default BuildingDetailsInsights;
